import { TypedDocumentNode, gql } from '@apollo/client';
import { defineMessages } from 'react-intl';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useTouchScreen } from '@sorare/core/src/hooks/device/useTouchScreen';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';

import { ChooseGameMode } from './ChooseGameMode';
import type {
  UnlockPro_currentUser,
  unlockProModeMutation,
  unlockProModeMutationVariables,
} from './__generated__/index.graphql';

type Props = {
  currentUser: UnlockPro_currentUser;
  onChooseCommon?: () => void;
  onChoosePro: (levelValue: LevelValue) => void;
  goProMode?: boolean;
};

const messages = defineMessages({
  tapToSelect: {
    id: 'UnlockPro.tapToSelect',
    defaultMessage: 'Tap above to select level',
  },
  clickToSelect: {
    id: 'UnlockPro.clickToSelect',
    defaultMessage: 'Click above to select level',
  },
});

export type LevelValue =
  | typeof Rarity.common
  | typeof Rarity.limited
  | typeof Rarity.rare
  | typeof Rarity.super_rare;

const UNLOCK_PRO_MODE_MUTATION = gql`
  mutation unlockProModeMutation($input: unlockProModeInput!) {
    unlockProMode(input: $input) {
      currentUser {
        slug
        forcedIntoOnboarding
        anyUserSportProfile(sport: FOOTBALL) {
          id
          ... on FootballUserSportProfile {
            id
            proUnlocked
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<unlockProModeMutation, unlockProModeMutationVariables>;

export const UnlockPro = ({
  currentUser,
  onChooseCommon,
  onChoosePro,
  goProMode,
}: Props) => {
  const getInteractionContext = useGetInteractionContext();

  const [mutate] = useMutation(UNLOCK_PRO_MODE_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const onGoPro = async (levelValue: LevelValue) => {
    await mutate({
      variables: {
        input: {
          sport: Sport.FOOTBALL,
          rarity: levelValue,
          interactionContext: getInteractionContext(),
        },
      },
    });
    onChoosePro(levelValue);
  };

  const isTouch = useTouchScreen();
  const tapInstructionMessage = isTouch
    ? messages.tapToSelect
    : messages.clickToSelect;

  return (
    <ChooseGameMode
      conversionCredit={currentUser.onboardingConversionCredit}
      goProMode={goProMode}
      tapInstructionMessage={tapInstructionMessage}
      onGoPro={() => {
        onGoPro(Rarity.limited);
      }}
      onChooseCommon={onChooseCommon}
    />
  );
};

UnlockPro.fragments = {
  currentUser: gql`
    fragment UnlockPro_currentUser on CurrentUser {
      slug
      onboardingConversionCredit(sport: FOOTBALL) {
        id
        percentageDiscount
        ...ChooseGameMode_conversionCredit
      }
    }
    ${ChooseGameMode.fragments.conversionCredit}
  ` as TypedDocumentNode<UnlockPro_currentUser>,
};
